import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Authorize from '../../../components/AuthComponent/Authorize';
import SaveButton from '../../../components/Buttons/SaveButton';
import ButtonBar from '../../../components/Fields/ButtonBar';
import DateControl from '../../../components/Fields/DateControl';
import DateField from '../../../components/Fields/DateField';
import Field from '../../../components/Fields/Field';
import FieldLabel from '../../../components/Fields/FieldLabel';
import FileUploadField from '../../../components/Fields/FileUploadField';
import NumberField from '../../../components/Fields/NumberField';
import SelectListField from '../../../components/Fields/SelectListField';
import TextareaField from '../../../components/Fields/TextareaField';
import TextboxField from '../../../components/Fields/TextboxField';
import { FieldConfig, FormConfig, ValidatorConfig } from '../../../components/Fields/Types';
import UserDefinedField from '../../../components/Fields/UserDefinedField';
import Form from '../../../components/Form/Form';
import Title from '../../../components/Headers/Title';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../../components/Spinner/SpinnerOverlay';
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import { useNotifications } from '../../../contexts/notificationContext';
import AssociationTypes from '../../../enums/AssociationTypes';
import ItemAttachmentCreateModel from '../../../models/ItemAttachmentCreateModel';
import ItemReferenceModel from '../../../models/ItemBrandModel';
import ItemEmployeeModel from '../../../models/ItemEmployeeModel';
import ItemTypeModel from '../../../models/ItemTypeModel';
import UserDefinedFieldValueModel from '../../../models/UserDefinedFieldValueModel';
import DataService from '../../../services/data.service';
import { Result } from '../../../types/Result';
import FormatUtils from '../../../utils/FormatUtils';
import Unauthorized from '../../Authentication/Unauthorized';
import ItemEmployeeField from './ItemEmployeeField';

interface ItemCreateModel {
    id?: number
    projectId?: number
    billOfMaterialId?: number
    name: string
    itemTypeId?: number
    itemType?: ItemTypeModel
    description: string
    productCategoryId?: number
    brandId?: number
    divisionId?: number
    licenseId?: number
    bomTemplateId?: number
    isDefaultBillOfMaterialItem: boolean
    isBillOfMaterialItem: boolean

    employees: ItemEmployeeModel[]
    userDefinedFields: UserDefinedFieldValueModel[]
    attachments: ItemAttachmentCreateModel[]
    itemReferences: ItemReferenceModel[]
    nrOfItems?: number
    beginDate: string
    endDate?: string
}

interface ICreateItemProps {
    projectId?: number
    projectTypeId?: number
    billOfMaterialId?: number
    itemTypeId?: number
    onItemCreated: (number) => void
}

const CreateItemView: React.FC<ICreateItemProps> = ({ projectId, projectTypeId, billOfMaterialId, itemTypeId, onItemCreated }) => {
    const [itemType, setItemType] = useState<ItemTypeModel>()
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const [showSpinner, setShowSpinner] = useState(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedForItemType } = useAuth();

    const [formData, setFormData] = useState<ItemCreateModel>({
        projectId: projectId,
        billOfMaterialId: billOfMaterialId,
        itemTypeId: itemTypeId, //?? 0,

        name: '',
        description: '',
        //productCategoryId: 0,
        //brandId: 0,
        bomTemplateId: 0,
        isDefaultBillOfMaterialItem: false,
        isBillOfMaterialItem: false,

        employees: [],
        userDefinedFields: [],
        attachments: [],
        itemReferences: [],
        nrOfItems: 1,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: ''
    });

    var itemTypesUrl = 'api/v1/selectlist/ItemTypes?activeOnly=true&applicationFunctionId=' + ApplicationFunctions.CreateItem
    if (projectTypeId)
        itemTypesUrl += '&projectTypeId=' + projectTypeId

    const itemTypes = useSelectListOptions(itemTypesUrl, '-- Select itemtype');
    const productCategories = useSelectListOptions('api/v1/selectlist/ProductCategoryNameTreeLeaves/?activeOnly=true&itemTypeId=' + formData.itemTypeId, '-- Select category');
    const brands = useSelectListOptions('api/v1/selectlist/BrandNameTreeLeaves?activeOnly=true', '-- Select brand');
    const bomTemplates = useSelectListOptions('api/v1/selectlist/BillOfMaterialsTemplates?itemTypeId=' + formData.itemTypeId, '-- Select BOM template');
    const divisions = useSelectListOptions('api/v1/selectlist/Divisions?activeOnly=true', '-- Select division');

    useEffect(() => {
        if (formData.itemTypeId) {
            DataService.getById<ItemTypeModel>('api/v1/itemtypes/', formData.itemTypeId)
                .then(response => {
                    setItemType(response);
                })
                .catch(e => handleException(e));

            DataService.get<UserDefinedFieldValueModel[]>('api/v1/itemrevisionuserdefinedfields/fornewitem/' + formData.itemTypeId)
                .then(response => {
                    formData.userDefinedFields = response;
                    setFormData({ ...formData });
                })
                .catch(e => handleException(e));

            DataService.get<ItemAttachmentCreateModel[]>('api/v1/itemattachments/fornewitem/' + formData.itemTypeId)
                .then(response => {
                    formData.attachments = response;
                    setFormData({ ...formData });
                })
                .catch(e => handleException(e));
        }
    }, [formData.itemTypeId]);

    useEffect(() => {
        if (formData.itemTypeId) {
            DataService.post<ItemEmployeeModel[]>('api/v1/itememployees/fornewitem/', formData)
                .then(response => {
                    formData.employees = response;
                    setFormData({...formData});
                })
                .catch(e => handleException(e));
        }
    }, [formData.itemTypeId, formData.productCategoryId, formData.brandId]);

    async function submit() {
        if (formData) {
            setShowSpinner(true)
            await Promise.all(formData.attachments.map(async (attachment) => {
                if (attachment.file) {
                    attachment.attachmentName = attachment.file.name;
                    let fileResponse = await DataService.createFile(attachment.file);
                    if (fileResponse.isSuccesful === true && fileResponse.data) {
                        attachment.fileResult = fileResponse.data;
                    }
                }
            }));

            DataService
                .create<number>(`api/v1/items/`, formData)
                .then(response => {
                    setShowSpinner(false)
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data) {
                        onItemCreated(response.data);
                    }
                })
                .catch(e => { setShowSpinner(false); handleException(e); });
        }
    }

    let showCategory = true
    let categoryValidators: ValidatorConfig[] = []
    let showBrand = true
    let brandValidators: ValidatorConfig[] = []
    if (itemType) {
        if (itemType.productCategoryAssociationTypeId == AssociationTypes.NoAssociation)
            showCategory = false;
        if (itemType.productCategoryAssociationTypeId == AssociationTypes.RequiredSingleAssociation || itemType.productCategoryAssociationTypeId == AssociationTypes.MinimumOneRequiredAssociation)
            categoryValidators.push({ type: 'isRequired' });

        if (itemType.brandAssociationTypeId == AssociationTypes.NoAssociation)
            showBrand = false;
        if (itemType.brandAssociationTypeId == AssociationTypes.RequiredSingleAssociation || itemType.brandAssociationTypeId == AssociationTypes.MinimumOneRequiredAssociation)
            brandValidators.push({ type: 'isRequired' });
    }

    let fields: FieldConfig[] = [
        { name: 'itemTypeId', label: 'Item type', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
        { name: 'name', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
        { name: 'customerSKU', label: 'Customer SKU', labelSize: 3 },
        { name: 'licensorSKU', label: 'Licensor SKU', labelSize: 3 },
        { name: 'productCategoryId', label: 'Category', labelSize: 3, validationRules: categoryValidators },
        { name: 'brandId', label: 'Brand', labelSize: 3, validationRules: brandValidators },
        { name: 'divisionId', label: 'Division', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
        { name: 'description', label: 'Description', labelSize: 3 },
        { name: 'bomTemplateId', label: 'BOM template', labelSize: 3 },
        { name: 'beginDate', label: 'Active from', labelSize: 3 , validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
        { name: 'endDate', label: 'Active until', labelSize: 3, validationRules: [{ type: 'isDate' }] },
        { name: 'defaultBomId', label: 'Version', labelSize: 3 },
    ];

    if (projectId)
        fields.push({ name: 'nrOfItems', label: 'Number of items', labelSize: 3, validationRules: [{ type: 'isRequired' }] })

    if (formData && formData.employees) {
        formData.employees.forEach((value, index) => {
            let label = value.functionalRole.name;
            let validationRules: ValidatorConfig[] = []
            if (value.isRequired)
                validationRules.push({ type: 'isRequired' });

            fields.push({ name: 'employees.' + index + '.employeeId', label: label, labelSize: 3, validationRules });
        });
    }

    if (formData && formData.attachments) {
        formData.attachments.forEach((value, index) => {
            if (value.attachmentType) {
                let label = value.attachmentType.name;
                let validationRules: ValidatorConfig[] = []
                if (value.attachmentType.imagePriority === 1)
                    validationRules.push({ type: 'isRequired' });

                fields.push({ name: 'attachments.' + index + '.file', label: label, labelSize: 3, validationRules });
            }
        });
    }

    if (formData && formData.userDefinedFields) {
        formData.userDefinedFields.forEach((value, index) => {
            let label = value.userDefinedField.name;
            let validationRules: ValidatorConfig[] = []
            if (value.userDefinedField.required)
                validationRules.push({ type: 'isRequired' });

            fields.push({ name: 'userDefinedFields.' + index + '.value', label: label, labelSize: 3, validationRules });
        });
    }

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!isAuthorizedForItemType(ApplicationFunctions.CreateItem))
        return (<Unauthorized />);

    if (!formData)
        return <></>;

    return (
        <div className='main-content'>
            <Form>
                <Form.Content>
                    <Container fluid>
                        <Row>
                            <Col md='6'>
                                <Title text='New item' size={5} />
                                {!itemTypeId && <SelectListField {...getFieldProps('itemTypeId')} options={itemTypes} />}
                                <TextboxField {...getFieldProps('name')} />
                                {itemType && itemType.hasCustomerSKU && < TextboxField {...getFieldProps('customerSKU')} />}
                                {itemType && itemType.hasLicensorSKU && < TextboxField {...getFieldProps('licensorSKU')} />}
                                {showCategory && <SelectListField {...getFieldProps('productCategoryId')} options={productCategories} />}
                                {showBrand && <SelectListField {...getFieldProps('brandId')} options={brands} />}
                                <SelectListField {...getFieldProps('divisionId')} options={divisions} />
                                {bomTemplates && bomTemplates.length > 1 && <SelectListField {...getFieldProps('bomTemplateId')} options={bomTemplates} />}
                                <TextareaField {...getFieldProps('description')} rows={3} cols={50} />
                                <DateField {...getFieldProps('beginDate')} />
                                <DateField {...getFieldProps('endDate')} />
                                {projectId && (<NumberField {...getFieldProps('nrOfItems')}/>)}
                                {formData.employees && formData.employees.length > 0 && <Title text='Employees' size={5} />}
                                {formData.employees && (
                                    formData.employees.map((itemEmployee: ItemEmployeeModel, index: number) => {
                                        return <ItemEmployeeField {...getFieldProps(`employees.${index}.employeeId`)} itemEmployee={itemEmployee} key={'employee-' + index} />
                                    })
                                )}
                                {formData.attachments && formData.attachments.length > 0 && <Title text='Attachments' size={5} />}
                                {formData.attachments && (
                                    formData.attachments.map((attachment: ItemAttachmentCreateModel, index: number) => {
                                        return <FileUploadField {...getFieldProps(`attachments.${index}.file`)} key={'attachment-' + index} />
                                    })
                                )}
                            </Col>
                            <Col md='6'>
                                {formData.userDefinedFields && formData.userDefinedFields.length > 0 && <Title text='Detail item info' size={5} />}
                                {formData.userDefinedFields && (
                                    formData.userDefinedFields.map((udf: UserDefinedFieldValueModel, index: number) => {
                                        return <UserDefinedField
                                            {...getFieldProps(`userDefinedFields.${index}.value`)}
                                            userDefinedFieldValue={udf}
                                            visible={udf.userDefinedField.visibleNew}
                                            key={`udf.${index}`}
                                        />
                                    })
                                )}
                            </Col>
                        </Row>
                    </Container>
                </Form.Content>
                <Form.Footer>
                    <Row>
                        <Col md='6'>
                            <ButtonBar offset={3}>
                                <Authorize applicationFunction={ApplicationFunctions.CreateItem} itemTypeId={formData.itemTypeId}>
                                    <SaveButton  {...getSubmitButtonProps()} />
                                </Authorize>
                            </ButtonBar>
                        </Col>
                        <Col md='6' className='hidden-md'>
                            <ButtonBar offset={0}>
                            </ButtonBar>
                        </Col>
                    </Row>
                </Form.Footer>
            </Form>
            {showSpinner && <SpinnerOverlay />}

        </div>
    );
}
export default CreateItemView;